import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import type { TaskRow } from './dispatch'

const api = clientFor('/dsp/api')

/** 行车任务状态机：planned → departed → arrived；planned / departed → cancelled */
export type TripStatus = 'planned' | 'departed' | 'arrived' | 'cancelled'

/** 可在页面上触发的行车任务动作，与后端 POST /trips/:id/<action> 一一对应 */
export type TripAction = 'depart' | 'arrive' | 'cancel'

/** 行车任务（一次出车）。字段与 services/dsp/internal/trip/model.go 的 Trip 一一对应。 */
export interface Trip {
  id: number
  tenant_id: number
  /** 行车任务号，前缀 PC */
  doc_no: string
  route_id: number
  vehicle_id: number
  driver_id: number
  /** 发车时由后端写入；新建入参里没有这个字段 */
  depart_at: string | null
  arrive_at: string | null
  status: TripStatus
  load_desc: string
  remark: string
  created_by: string
  created_at: string
  updated_at: string
}

/** 列表行：单据 + 车牌 / 司机 / 线路名 + 所载任务数与装载量 */
export interface TripRow extends Trip {
  plate_no: string
  driver_name: string
  route_name: string
  task_count: number
  load_weight_kg: number
  load_volume_cm3: number
}

/** 详情：单据 + 关联主数据名 + 所载调度任务（由后端拼装） */
export interface TripDetail extends Trip {
  plate_no: string
  driver_name: string
  route_name: string
  task_count: number
  tasks: TaskRow[]
}

/**
 * 新建行车任务的入参：车辆必填（派车与容量校验都挂在车上），线路与司机可留空；
 * 后端 trip.Input 没有计划发车时间字段，depart_at 只在「发车」时由服务端写。
 */
export interface TripInput {
  vehicle_id: number
  route_id?: number
  driver_id?: number
  load_desc?: string
  remark?: string
}

export interface TripQuery extends PageQuery {
  keyword?: string
  status?: string
  vehicle_id?: number
  route_id?: number
}

/** 与后端 services/dsp/internal/trip/model.go 的 StatusNames 同源两份，改一边必须改另一边 */
export const STATUS_LABELS: Record<TripStatus, string> = {
  planned: '计划中',
  departed: '已发车',
  arrived: '已到达',
  cancelled: '已取消',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<TripStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> = {
  planned: 'primary',
  departed: 'warning',
  arrived: 'success',
  cancelled: 'danger',
}

/** 各状态下允许的动作，抄后端 internal/trip/service.go 的状态机 */
const ACTIONS: Record<TripStatus, TripAction[]> = {
  planned: ['depart', 'cancel'],
  departed: ['arrive', 'cancel'],
  arrived: [],
  cancelled: [],
}

/** 状态能不能触发某个动作（前端只做展示，最终以后端 409 为准） */
export function canAct(status: TripStatus, action: TripAction): boolean {
  return ACTIONS[status].includes(action)
}

export const listTrips = (q: TripQuery): Promise<PageResult<TripRow>> =>
  api.get<PageResult<TripRow>>('/trips', { ...q })

export const getTrip = (id: number): Promise<TripDetail> => api.get<TripDetail>(`/trips/${id}`)

export const createTrip = (input: TripInput): Promise<Trip> => api.post<Trip>('/trips', input)

const act = (id: number, action: TripAction): Promise<void> => api.post<void>(`/trips/${id}/${action}`, {})

export const departTrip = (id: number): Promise<void> => act(id, 'depart')
export const arriveTrip = (id: number): Promise<void> => act(id, 'arrive')
export const cancelTrip = (id: number): Promise<void> => act(id, 'cancel')

/** 下拉参照用的全量行车任务：后端 size 上限 200 */
export const listTripOptions = (): Promise<TripRow[]> =>
  listTrips({ page: 1, size: 200 }).then((page) => page.list)

/** 待挂单的行车任务：手工派车只能挂在「计划中」的任务上 */
export const listPlannedTrips = (): Promise<TripRow[]> =>
  listTrips({ page: 1, size: 200, status: 'planned' }).then((page) => page.list)
